import { ButtonHTMLAttributes, ReactNode, useState } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** If true, clicking the button toggles a persistent selected state */
  toggleable?: boolean;
}

function Button({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  toggleable = false,
  ...props
}: ButtonProps) {
  const baseClasses =
    'inline-flex items-center justify-center rounded-lg font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50';

  const variantClasses = {
    primary:
      'bg-primary-600 text-white shadow-md hover:bg-primary-700 disabled:hover:bg-primary-600',
    secondary:
      'border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:hover:bg-white',
    outline:
      'border-2 border-primary-600 bg-transparent text-primary-600 hover:bg-primary-50 disabled:hover:bg-transparent',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  const combinedClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  // Determine if component is controlled by incoming aria-pressed
  const incomingAriaPressed = props['aria-pressed'];
  const isControlled = incomingAriaPressed !== undefined;

  const [pressed, setPressed] = useState<boolean>(
    isControlled ? !!incomingAriaPressed : false
  );

  const handleClick: ButtonProps['onClick'] = (e) => {
    if (toggleable && !isControlled) {
      setPressed((p) => !p);
    }

    props.onClick?.(e);
  };

  const isPressed = isControlled ? Boolean(incomingAriaPressed) : pressed;

  return (
    <button
      className={`${combinedClasses} ${isPressed ? 'item-selected' : ''}`}
      aria-pressed={isPressed}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
}

export default Button;
